import React, { useState, useEffect, useMemo } from "react"
import Navigation from "../components/Navigation"
import Header from "../components/Header"
import NotificationToaster from "../components/NotificationToaster"
import Login from "../components/Login"
import LogoutConfirmModal from "../components/LogoutConfirmModal"
import { useData } from "../contexts/DataContext"
import useLogout from "../hooks/useLogout"
import {
    componentServices,
    groupReportServices,
    propertyServices,
    excelImportServices,
    utils
} from "../services/componentServices"

/* ─────────────────────────────────────────────────────────
   Constants
   ───────────────────────────────────────────────────────── */
const currencies = [
    { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
    { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham (Dubai)' },
    { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal' },
    { code: 'QAR', symbol: '﷼', name: 'Qatari Riyal' },
    { code: 'KWD', symbol: 'د.ك', name: 'Kuwaiti Dinar' },
    { code: 'BHD', symbol: '.د.ب', name: 'Bahraini Dinar' },
    { code: 'OMR', symbol: '﷼', name: 'Omani Rial' }
];

/* ─────────────────────────────────────────────────────────
   Shared Button Components (mini design system)
   ───────────────────────────────────────────────────────── */
const BtnPrimary = ({ children, onClick, disabled, className = "" }) => (
    <button onClick={onClick} disabled={disabled}
        className={`h-10 px-4 inline-flex items-center gap-2 rounded-lg text-sm font-medium transition-all
        ${disabled ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow'}
        ${className}`}>
        {children}
    </button>
);
const BtnSecondary = ({ children, onClick, disabled, className = "" }) => (
    <button onClick={onClick} disabled={disabled}
        className={`h-10 px-4 inline-flex items-center gap-2 rounded-lg text-sm font-medium transition-all
        ${disabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'}
        ${className}`}>
        {children}
    </button>
);
const BtnOutline = ({ children, onClick, disabled, className = "" }) => (
    <button onClick={onClick} disabled={disabled}
        className={`h-10 px-4 inline-flex items-center gap-2 rounded-lg text-sm font-medium transition-all border
        ${disabled ? 'border-gray-200 text-gray-400 cursor-not-allowed' : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 bg-white'}
        ${className}`}>
        {children}
    </button>
);
const BtnDanger = ({ children, onClick, disabled, className = "" }) => (
    <button onClick={onClick} disabled={disabled}
        className={`h-10 px-4 inline-flex items-center gap-2 rounded-lg text-sm font-medium transition-all
        ${disabled ? 'bg-red-200 text-red-400 cursor-not-allowed' : 'bg-red-600 text-white hover:bg-red-700 shadow-sm'}
        ${className}`}>
        {children}
    </button>
);

/* SVG Icon helpers */
const IconPlus = ({ className = "w-4 h-4" }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
);
const IconUpload = ({ className = "w-4 h-4" }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" /></svg>
);
const IconDownload = ({ className = "w-4 h-4" }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
);
const IconTrash = ({ className = "w-4 h-4" }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
);
const IconEdit = ({ className = "w-4 h-4" }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
);
const IconCheck = ({ className = "w-4 h-4" }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
);
const IconClose = ({ className = "w-5 h-5" }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
);
const IconSearch = ({ className = "w-4 h-4" }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
);
const IconGroup = ({ className = "w-4 h-4" }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
);
const IconSpinner = ({ className = "w-5 h-5" }) => (
    <svg className={`${className} animate-spin`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
);
const IconClipboard = ({ className = "w-4 h-4" }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
);
const IconDocument = ({ className = "w-4 h-4" }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
);
const IconBox = ({ className = "w-4 h-4" }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
);

const ComponentsPage = ({
    currentUser,
    onLogout,
    onShowUserManagement,
    onTabChange,
    activeTab,
    sidebarOpen,
    onToggleSidebar,
}) => {
    const { savedComponents } = useData()
    const [notifications, setNotifications] = useState([])
    const [searchTerm, setSearchTerm] = useState("")
    const [sortBy, setSortBy] = useState("oldest")
    const [shouldRefresh, setShouldRefresh] = useState(false)
    const [showGlobalReport, setShowGlobalReport] = useState(true)
    const [showGroupReport, setShowGroupReport] = useState(false)
    const [currency, setCurrency] = useState("INR")
    const [filters, setFilters] = useState({
        minCost: "",
        maxCost: "",
        minGravity: "",
        maxGravity: "",
    })
    const [showAddGroupPopup, setShowAddGroupPopup] = useState(false)
    const [selectedComponents, setSelectedComponents] = useState([])
    const [groupName, setGroupName] = useState("")
    const [groupDescription, setGroupDescription] = useState("")
    const [savedGroups, setSavedGroups] = useState([])
    const [file, setFile] = useState(null)
    const [loading, setLoading] = useState(false)
    const [apiComponents, setApiComponents] = useState([])
    const [loadingComponents, setLoadingComponents] = useState(false)
    const [apiGroupReports, setApiGroupReports] = useState([])
    const [loadingGroupReports, setLoadingGroupReports] = useState(false)
    const [addedComponents, setAddedComponents] = useState([])
    const [loadingAddedComponents, setLoadingAddedComponents] = useState(false)
    const [currentPage, setCurrentPage] = useState(1)
    const [itemsPerPage, setItemsPerPage] = useState(10)
    const [showAddComponentModal, setShowAddComponentModal] = useState(false)
    const [newComponent, setNewComponent] = useState({
        name: "",
        cost: "",
        costUnit: localStorage.getItem("blendOptimizerCurrency") || "USD",
        gravity: "",
        minVolume: "",
        maxVolume: "",
        availableVolume: "",
        volumeUnit: localStorage.getItem("blendOptimizerVolumeUnit") || "bbl",
        density: "",
        selectedProperties: [],
        selectedGroups: [],
    })
    const [propertyType, setPropertyType] = useState("global")
    const [globalPropertyOptions, setGlobalPropertyOptions] = useState([])
    const [groupPropertyOptions, setGroupPropertyOptions] = useState([])
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [componentToDelete, setComponentToDelete] = useState(null)
    const [expandedGroupReport, setExpandedGroupReport] = useState(null)
    const [importedComponents, setImportedComponents] = useState([])
    const [showExcelImportPopup, setShowExcelImportPopup] = useState(false)
    const [globalProperties, setGlobalProperties] = useState([])
    const [propertyGroupsFromAPI, setPropertyGroupsFromAPI] = useState([])
    const [propertySetsFromAPI, setPropertySetsFromAPI] = useState([])
    const [loadingProperties, setLoadingProperties] = useState(false)
    const [excelPreviewData, setExcelPreviewData] = useState([])
    const [selectedPreviewComponents, setSelectedPreviewComponents] = useState([])
    const [showExcelPreview, setShowExcelPreview] = useState(false)
    const [editingComponent, setEditingComponent] = useState(null)
    const [showEditComponentPopup, setShowEditComponentPopup] = useState(false)
    const [editedComponentData, setEditedComponentData] = useState({})
    const [globalLoading, setGlobalLoading] = useState(false)
    const [showEditGroupPopup, setShowEditGroupPopup] = useState(false)
    const [editingGroup, setEditingGroup] = useState(null)
    const [groupComponentsToAdd, setGroupComponentsToAdd] = useState([])
    const [editingGroupName, setEditingGroupName] = useState("") // New state for editing group name

    const [selectedForBulkDelete, setSelectedForBulkDelete] = useState([])
    const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false)
    const [excelData, setExcelData] = useState([]);
    const [showExcelModal, setShowExcelModal] = useState(false);
    const [excelLoading, setExcelLoading] = useState(false);
    const [selectedExcelComponents, setSelectedExcelComponents] = useState([]);
    const [sourceFilter, setSourceFilter] = useState("all");
    const [groupSearchTerm, setGroupSearchTerm] = useState("");
    
    const {
        showLogoutConfirm,
        handleLogoutClick,
        handleConfirmLogout,
        handleCancelLogout
    } = useLogout()

    const fetchComponents = async () => {
    setLoadingComponents(true);
    try {
        const components = await componentServices.fetchComponents();
        // Sort components by creation date (oldest first - new components appear at bottom)
        const sortedComponents = components.sort((a, b) => 
            new Date(a.createdAt || a.timestamp) - new Date(b.createdAt || b.timestamp)
        );
        setApiComponents(sortedComponents);
    } catch (error) {
        console.error("Error fetching components:", error);
        setApiComponents([]);
    } finally {
        setLoadingComponents(false);
    }
};

    const fetchAddedComponents = async () => {
    setLoadingAddedComponents(true);
    try {
        const components = await componentServices.fetchAddedComponents();
        // Sort added components by creation date (oldest first - new components appear at bottom)
        const sortedComponents = (components || []).sort((a, b) => 
            new Date(a.createdAt || a.timestamp) - new Date(b.createdAt || b.timestamp)
        );
        setAddedComponents(sortedComponents);
    } catch (error) {
        console.error("Error fetching added components:", error);
        setAddedComponents([]);
    } finally {
        setLoadingAddedComponents(false);
    }
};

    const fetchGroupReports = async () => {
        setLoadingGroupReports(true);
        try {
            const reports = await groupReportServices.fetchGroupReports();
            setApiGroupReports(reports);
        } catch (error) {
            console.error("Error fetching group reports:", error);
            addNotification("error", `Failed to load group reports: ${error.message}`);
        } finally {
            setLoadingGroupReports(false);
        }
    };

    const fetchPropertiesFromAPIs = async () => {
        setLoadingProperties(true);
        try {
            const properties = await propertyServices.fetchAllProperties();
            setGlobalProperties(properties.globalProperties);
            setPropertyGroupsFromAPI(properties.propertyGroups);
            setPropertySetsFromAPI(properties.propertySets);
        } catch (error) {
            console.error("Error fetching properties:", error);
        } finally {
            setLoadingProperties(false);
        }
    };

    const saveComponentToMongoDB = async (componentData) => {
        return await componentServices.saveComponent(componentData);
    };

    const updateComponentInMongoDB = async (id, componentData) => {
        return await componentServices.updateComponent(id, componentData);
    };

    const deleteComponentFromMongoDB = async (id) => {
        return await componentServices.deleteComponent(id);
    };

    const createAddedComponent = async (componentData) => {
        return await componentServices.createAddedComponent(componentData);
    };

    const updateAddedComponent = async (id, componentData) => {
        return await componentServices.updateAddedComponent(id, componentData);
    };

    const deleteAddedComponent = async (id) => {
        return await componentServices.deleteAddedComponent(id);
    };

    const createGroupReportFromGroup = async (groupData) => {
        return await groupReportServices.createGroupReport(groupData);
    };

    const updateGroupReport = async (id, groupData) => {
        return await groupReportServices.updateGroupReport(id, groupData);
    };

    const deleteGroupReport = async (id) => {
        return await groupReportServices.deleteGroupReport(id);
    };

    const onChooseFile = (e) => {
        const selectedFile = e.target.files?.[0] || null;
        setFile(selectedFile);
        if (selectedFile) {
            addNotification("info", `Selected file: ${selectedFile.name}`);
            setExcelPreviewData([]);
            setShowExcelPreview(false);
        }
    };

    const loadExcelForPreview = async () => {
        if (!file) {
            addNotification("error", "Please choose an Excel file first");
            return;
        }

        setLoading(true);
        try {
            const previewComponents = await excelImportServices.processExcelForPreview(file);
            setExcelPreviewData(previewComponents);
            setSelectedPreviewComponents(previewComponents.map(comp => comp.id));
            setShowExcelPreview(true);
            addNotification("success", `Loaded ${previewComponents.length} components for preview`);
        } catch (error) {
            console.error("Error loading Excel:", error);
            addNotification("error", `Failed to process Excel file: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const importPreviewedComponents = async () => {
        const selectedComponents = excelPreviewData.filter(comp =>
            selectedPreviewComponents.includes(comp.id)
        );

        if (selectedComponents.length === 0) {
            addNotification("error", "Please select at least one component to import");
            return;
        }

        setLoading(true);
        try {
            const { importedComponents, importedCount, errorCount } = await excelImportServices.importComponents(selectedComponents);

            if (importedComponents.length > 0) {
                const existingComponents = JSON.parse(localStorage.getItem('addedComponents') || '[]');
                // Add imported components to the end (bottom)
                const updatedComponents = [...existingComponents, ...importedComponents];
                localStorage.setItem('addedComponents', JSON.stringify(updatedComponents));

                window.dispatchEvent(new CustomEvent('componentsUpdated', {
                    detail: {
                        addedComponents: updatedComponents,
                        type: 'excel-imported'
                    }
                }));
            }

            addNotification("success", `Successfully imported ${importedCount} components to database`);

            if (errorCount > 0) {
                addNotification("warning", `${errorCount} components failed to import`);
            }

            setFile(null);
            setExcelPreviewData([]);
            setSelectedPreviewComponents([]);
            setShowExcelPreview(false);
            setShowExcelImportPopup(false);

            const fileInput = document.getElementById('excel-file-components-popup');
            if (fileInput) fileInput.value = '';

            setShouldRefresh(true);

            await fetchComponents();
            await fetchAddedComponents();

        } catch (error) {
            console.error("Error importing components:", error);
            addNotification("error", `Failed to import components: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const getCurrencySymbol = (currCode = null) => {
        return utils.getCurrencySymbol(currCode || currency);
    };

    const generateUniqueKey = (component, index, type) => {
        return utils.generateUniqueKey(component, index, type);
    };

    const getSelectedPropertyNames = (component) => {
        return utils.getSelectedPropertyNames(component);
    };

    const addNotification = (type, message) => {
        const id = Date.now()
        setNotifications((prev) => [...prev, { id, type, message }])

        setTimeout(() => {
            setNotifications((prev) => prev.filter((n) => n.id !== id))
        }, 3000)
    }

    useEffect(() => {
        const savedCurrency = localStorage.getItem("blendOptimizerCurrency")
        if (savedCurrency) {
            setCurrency(savedCurrency)
        }

        fetchComponents();
        fetchGroupReports();
        fetchAddedComponents();
        fetchPropertiesFromAPIs();
    }, [currentUser, shouldRefresh])

    useEffect(() => {
        setCurrentPage(1)
    }, [searchTerm, sortBy, filters, sourceFilter, showGlobalReport, showGroupReport])

    useEffect(() => {
        if (showAddComponentModal) {
            loadPropertyAndGroupOptions()
        }
    }, [showAddComponentModal])

        

 const loadPropertyAndGroupOptions = () => {
    const { globalPropertyOptions: globalOptions, groupPropertyOptions: groupOptions } = 
        utils.preparePropertyOptions(globalProperties, propertyGroupsFromAPI);
    
    // Force 0 as default value instead of 50
    const processedGlobalOptions = globalOptions.map(prop => ({
        ...prop,
        defaultValue: prop.defaultValue === 50 ? 0 : prop.defaultValue
    }));
    
    const processedGroupOptions = groupOptions.map(group => ({
        ...group,
        defaultValue: group.defaultValue === 50 ? 0 : group.defaultValue
    }));
    
    setGlobalPropertyOptions(processedGlobalOptions);
    setGroupPropertyOptions(processedGroupOptions);
}

   const handlePropertySelection = (item) => {
    if (propertyType === "global") {
        setNewComponent((prev) => {
            const isSelected = prev.selectedProperties.some(p => p.name === item.name)
            if (isSelected) {
                return {
                    ...prev,
                    selectedProperties: prev.selectedProperties.filter(p => p.name !== item.name)
                }
            } else {
                return {
                    ...prev,
                    selectedProperties: [...prev.selectedProperties, {
                        ...item,
                        editableDefaultValue: 0 // Force 0 instead of item.defaultValue
                    }]
                }
            }
        })
    } else {
        setNewComponent((prev) => {
            const isSelected = prev.selectedGroups.some(g => g.name === item.name)
            if (isSelected) {
                return {
                    ...prev,
                    selectedGroups: prev.selectedGroups.filter(g => g.name !== item.name)
                }
            } else {
                return {
                    ...prev,
                    selectedGroups: [...prev.selectedGroups, {
                        ...item,
                        editableDefaultValue: 0 // Force 0 instead of item.defaultValue
                    }]
                }
            }
        })
    }
}

    const handleDefaultValueChange = (itemName, newValue, isGlobal = true) => {
        if (isGlobal) {
            setNewComponent(prev => ({
                ...prev,
                selectedProperties: prev.selectedProperties.map(prop =>
                    prop.name === itemName
                        ? { ...prop, editableDefaultValue: newValue }
                        : prop
                )
            }));
        } else {
            setNewComponent(prev => ({
                ...prev,
                selectedGroups: prev.selectedGroups.map(group =>
                    group.name === itemName
                        ? { ...group, editableDefaultValue: newValue }
                        : group
                )
            }));
        }
    }

const handleSelectAllProperties = () => {
    if (propertyType === "global") {
        setNewComponent((prev) => {
            if (prev.selectedProperties.length === globalPropertyOptions.length) {
                return { ...prev, selectedProperties: [] }
            } else {
                return {
                    ...prev,
                    selectedProperties: globalPropertyOptions.map(prop => ({
                        ...prop,
                        editableDefaultValue: 0 // Force 0
                    }))
                }
            }
        })
    } else {
        setNewComponent((prev) => {
            if (prev.selectedGroups.length === groupPropertyOptions.length) {
                return { ...prev, selectedGroups: [] }
            } else {
                return {
                    ...prev,
                    selectedGroups: groupPropertyOptions.map(group => ({
                        ...group,
                        editableDefaultValue: 0 // Force 0
                    }))
                }
            }
        })
    }
}

    //         const gravityValue = newComponent.gravity !== "" ? Number(newComponent.gravity) : 0;

    //         console.log("Saving component with gravity:", gravityValue); 

    //         // Save to MongoDB
    //         const result = await saveComponentToMongoDB(componentData);

    //         if (result.success) {
    //             addNotification("success", `Component "${componentData.name}" added successfully to database`);

    //             await fetchComponents();
    //             await fetchAddedComponents();

    const handleAddComponent = async () => {
    if (!newComponent.name.trim()) {
        addNotification("error", "Component name is required")
        return
    }

    if (newComponent.selectedProperties.length === 0 && newComponent.selectedGroups.length === 0) {
        addNotification("error", "Please select at least one property or group")
        return
    }

    try {
        const allSelectedItems = [...newComponent.selectedProperties, ...newComponent.selectedGroups];
        const selectedPropertyText = allSelectedItems.map(item =>
            `${item.name} (${item.uom}) - Min: ${item.minValue}, Max: ${item.maxValue}, Default: ${item.editableDefaultValue || 0}`
        ).join("; ");

        const gravityValue = newComponent.gravity !== "" ? Number(newComponent.gravity) : 0;

        const componentData = {
            name: newComponent.name.trim(),
            cost: newComponent.cost ? Number(newComponent.cost) : 0,
            costUnit: newComponent.costUnit,
            gravity: gravityValue, 
            selectedProperty: selectedPropertyText,
            selectedProperties: newComponent.selectedProperties.map(prop => ({
                ...prop,
                defaultValue: prop.editableDefaultValue || 0 // Force 0
            })),
            selectedGroups: newComponent.selectedGroups.map(group => ({
                ...group,
                defaultValue: group.editableDefaultValue || 0 // Force 0
            })),
            source: "manual",
            minVolume: newComponent.minVolume ? Number(newComponent.minVolume) : 0,
            maxVolume: newComponent.maxVolume ? Number(newComponent.maxVolume) : 1000,
            availableVolume: newComponent.availableVolume ? Number(newComponent.availableVolume) : 500,
            volumeUnit: newComponent.volumeUnit,
            density: newComponent.density ? Number(newComponent.density) : 0,
        }

        console.log("Saving component with gravity:", gravityValue); 

        // Save to MongoDB
        const result = await saveComponentToMongoDB(componentData);

        if (result.success) {
            addNotification("success", `Component "${componentData.name}" added successfully to database`);

            const existingComponents = JSON.parse(localStorage.getItem('addedComponents') || '[]');
            const updatedComponents = [...existingComponents, {
                ...componentData,
                _id: result.data?._id || `local-${Date.now()}`,
                id: result.data?._id || `local-${Date.now()}`,
                createdAt: new Date().toISOString(),
                source: 'manual',
                sourceDisplay: 'Added Component'
            }];
            localStorage.setItem('addedComponents', JSON.stringify(updatedComponents));

            window.dispatchEvent(new CustomEvent('componentsUpdated', {
                detail: {
                    addedComponents: updatedComponents,
                    type: 'added'
                }
            }));

            // Set sort to oldest to ensure new component appears at bottom
            setSortBy("oldest");
            
            // Fetch components to refresh the list (sorted oldest first, so new component appears at bottom)
            await fetchComponents();
            await fetchAddedComponents();

            setShowAddComponentModal(false);
            
            // Scroll to bottom to show the newly added component after a short delay
            setTimeout(() => {
                window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
            }, 300);
            setNewComponent({
                name: "",
                cost: "",
                costUnit: localStorage.getItem("blendOptimizerCurrency") || "USD",
                gravity: "",
                minVolume: "",
                maxVolume: "",
                availableVolume: "",
                volumeUnit: localStorage.getItem("blendOptimizerVolumeUnit") || "bbl",
                density: "",
                selectedProperties: [],
                selectedGroups: [],
            });
            setPropertyType("global");
            setShouldRefresh(true);
        }
    } catch (error) {
        console.error("Error adding component:", error);
        addNotification("error", `Failed to add component: ${error.message}`);
    }
}

    const handleBulkDeleteSelection = (componentId) => {
        setSelectedForBulkDelete(prev => {
            if (prev.includes(componentId)) {
                return prev.filter(id => id !== componentId);
            } else {
                return [...prev, componentId];
            }
        });
    };

    const handleSelectAllForBulkDelete = () => {
        const allComponents = getUniqueComponents();
        if (selectedForBulkDelete.length === allComponents.length) {
            setSelectedForBulkDelete([]);
        } else {
            setSelectedForBulkDelete(allComponents.map(comp => comp._id || comp.id));
        }
    };

    const handleBulkDelete = () => {
        if (selectedForBulkDelete.length === 0) {
            addNotification("error", "Please select at least one component to delete");
            return;
        }
        setShowBulkDeleteConfirm(true);
    };

    const handleBulkDeleteConfirm = async () => {
        if (selectedForBulkDelete.length === 0) return;

        try {
            const allComponents = getUniqueComponents();
            const componentsToDelete = allComponents.filter(comp => 
                selectedForBulkDelete.includes(comp._id || comp.id)
            );

            let successCount = 0;
            let errorCount = 0;

            for (const component of componentsToDelete) {
                try {
                    let result;
                    const isAddedComponent = component.source === "manual";

                    if (isAddedComponent) {
                        result = await deleteAddedComponent(component._id);
                    } else {
                        result = await deleteComponentFromMongoDB(component._id);
                    }

                    if (result.success) {
                        successCount++;
                    } else {
                        errorCount++;
                    }
                } catch (error) {
                    console.error(`Error deleting component ${component.name}:`, error);
                    errorCount++;
                }
            }

            if (successCount > 0) {
                addNotification("success", `Successfully deleted ${successCount} component(s)`);
                await fetchComponents();
                await fetchAddedComponents();
                setShouldRefresh(true);
            }

            if (errorCount > 0) {
                addNotification("error", `Failed to delete ${errorCount} component(s)`);
            }

            setSelectedForBulkDelete([]);
        } catch (error) {
            console.error("Error in bulk delete:", error);
            addNotification("error", "Failed to delete components");
        }

        setShowBulkDeleteConfirm(false);
    };

    const handleDeleteComponent = (component, isAddedComponent = false) => {
        setComponentToDelete({ ...component, isAddedComponent })
        setShowDeleteConfirm(true)
    }

    const handleDeleteConfirm = async () => {
        if (!componentToDelete) return

        try {
            let result;

            if (componentToDelete.isAddedComponent) {
                result = await deleteAddedComponent(componentToDelete._id);
            } else {
                result = await deleteComponentFromMongoDB(componentToDelete._id);
            }

            if (result.success) {
                addNotification("success", `Component "${componentToDelete.name}" deleted successfully`);
                await fetchComponents();
                await fetchAddedComponents();
            }

            setShouldRefresh(true);
        } catch (error) {
            console.error("Error deleting component:", error);
            addNotification("error", "Failed to delete component");
        }

        setShowDeleteConfirm(false);
        setComponentToDelete(null);
    }

    const handleDeleteCancel = () => {
        setShowDeleteConfirm(false)
        setComponentToDelete(null)
    }

    const handleEditComponent = (component, isAddedComponent = false) => {
        setEditingComponent({ ...component, isAddedComponent });

        loadPropertyAndGroupOptions();

        const selectedProperties = component.selectedProperties || [];
        const selectedGroups = component.selectedGroups || [];

        setEditedComponentData({
            ...component,
            cost: component.cost !== undefined && component.cost !== null ? component.cost : "",
            gravity: component.gravity !== undefined && component.gravity !== null ? component.gravity : "",
            minVolume: component.minVolume || "",
            maxVolume: component.maxVolume || "",
            availableVolume: component.availableVolume || "",
            density: component.density || "",
            selectedProperties: selectedProperties.map(prop => ({
                ...prop,
                editableDefaultValue: prop.defaultValue
            })),
            selectedGroups: selectedGroups.map(group => ({
                ...group,
                editableDefaultValue: group.defaultValue
            })),
        })
        setShowEditComponentPopup(true)
    }

    const handleEditComponentSave = async () => {
        if (!editingComponent) return

        try {
            const allSelectedItems = [...(editedComponentData.selectedProperties || []), ...(editedComponentData.selectedGroups || [])];
            const selectedPropertyText = allSelectedItems.map(item =>
                `${item.name} (${item.uom}) - Min: ${item.minValue}, Max: ${item.maxValue}, Default: ${item.editableDefaultValue || item.defaultValue}`
            ).join("; ");

            const gravityValue = editedComponentData.gravity !== "" ? Number(editedComponentData.gravity) : 0;

            const updateData = {
                name: editedComponentData.name,
                cost: editedComponentData.cost ? Number(editedComponentData.cost) : 0,
                costUnit: editedComponentData.costUnit,
                gravity: gravityValue, 
                selectedProperty: selectedPropertyText,
                selectedProperties: (editedComponentData.selectedProperties || []).map(prop => ({
                    ...prop,
                    defaultValue: prop.editableDefaultValue || prop.defaultValue
                })),
                selectedGroups: (editedComponentData.selectedGroups || []).map(group => ({
                    ...group,
                    defaultValue: group.editableDefaultValue || group.defaultValue
                })),
                minVolume: editedComponentData.minVolume ? Number(editedComponentData.minVolume) : 0,
                maxVolume: editedComponentData.maxVolume ? Number(editedComponentData.maxVolume) : 1000,
                availableVolume: editedComponentData.availableVolume ? Number(editedComponentData.availableVolume) : 500,
                volumeUnit: editedComponentData.volumeUnit,
                density: editedComponentData.density ? Number(editedComponentData.density) : 0,
            };

            console.log("Updating component with gravity:", gravityValue); 

            let result;

            if (editingComponent.isAddedComponent) {
                result = await updateAddedComponent(editingComponent._id, updateData);
            } else {
                result = await updateComponentInMongoDB(editingComponent._id, updateData);
            }

            if (result.success) {
                addNotification("success", `Component "${updateData.name}" updated successfully`);
                await fetchComponents();
                await fetchAddedComponents();
            }

            setShowEditComponentPopup(false);
            setEditingComponent(null);
            setEditedComponentData({});
            setShouldRefresh(true);
        } catch (error) {
            console.error("Error updating component:", error);
            addNotification("error", "Failed to update component");
        }
    }

    const handleEditComponentCancel = () => {
        setShowEditComponentPopup(false)
        setEditingComponent(null)
        setEditedComponentData({})
    }

    const handleComponentFieldChange = (field, value) => {
        setEditedComponentData((prev) => ({
            ...prev,
            [field]: value
        }))
    }

    const handleEditPropertySelection = (item) => {

        //         const isSelected = currentProperties.some(p => p.name === item.name)

         if (propertyType === "global") {
        setEditedComponentData((prev) => {
            let currentProperties = prev.selectedProperties || [];

            const isSelected = currentProperties.some(p => p.name === item.name)

            if (isSelected) {
                currentProperties = currentProperties.filter(p => p.name !== item.name)
            } else {
                currentProperties = [...currentProperties, {
                    ...item,
                    editableDefaultValue: 0 // Force 0 instead of item.defaultValue
                }]
            }

            const allSelectedItems = [...currentProperties, ...(prev.selectedGroups || [])];
            const selectedPropertyText = allSelectedItems.map(item =>
                `${item.name} (${item.uom}) - Min: ${item.minValue}, Max: ${item.maxValue}, Default: ${item.editableDefaultValue || 0}`
            ).join("; ");

            return {
                ...prev,
                selectedProperties: currentProperties,
                selectedProperty: selectedPropertyText
            }
        })
        } else {
            setEditedComponentData((prev) => {
                let currentGroups = prev.selectedGroups || [];

                const isSelected = currentGroups.some(g => g.name === item.name)

                if (isSelected) {
                    currentGroups = currentGroups.filter(g => g.name !== item.name)
                } else {
                    currentGroups = [...currentGroups, {
                        ...item,
                        editableDefaultValue: item.defaultValue
                    }]
                }

                const allSelectedItems = [...(prev.selectedProperties || []), ...currentGroups];
                const selectedPropertyText = allSelectedItems.map(item =>
                    `${item.name} (${item.uom}) - Min: ${item.minValue}, Max: ${item.maxValue}, Default: ${item.editableDefaultValue || item.defaultValue}`
                ).join("; ");

                return {
                    ...prev,
                    selectedGroups: currentGroups,
                    selectedProperty: selectedPropertyText
                }
            })
        }
    }

    const handleEditDefaultValueChange = (itemName, newValue, isGlobal = true) => {
        if (isGlobal) {
            setEditedComponentData(prev => ({
                ...prev,
                selectedProperties: (prev.selectedProperties || []).map(prop =>
                    prop.name === itemName
                        ? { ...prop, editableDefaultValue: newValue }
                        : prop
                )
            }));
        } else {
            setEditedComponentData(prev => ({
                ...prev,
                selectedGroups: (prev.selectedGroups || []).map(group =>
                    group.name === itemName
                        ? { ...group, editableDefaultValue: newValue }
                        : group
                )
            }));
        }
    }

    const handleEditSelectAllProperties = () => {
        if (propertyType === "global") {
            setEditedComponentData((prev) => {
                const currentProperties = prev.selectedProperties || [];

                if (currentProperties.length === globalPropertyOptions.length) {
                    const allSelectedItems = [...(prev.selectedGroups || [])];
                    const selectedPropertyText = allSelectedItems.map(item =>
                        `${item.name} (${item.uom}) - Min: ${item.minValue}, Max: ${item.maxValue}, Default: ${item.editableDefaultValue || item.defaultValue}`
                    ).join("; ");

                    return {
                        ...prev,
                        selectedProperties: [],
                        selectedProperty: selectedPropertyText
                    }
                } else {
                    const updatedProperties = globalPropertyOptions.map(prop => ({
                        ...prop,
                        editableDefaultValue: prop.defaultValue
                    }));
                    const allSelectedItems = [...updatedProperties, ...(prev.selectedGroups || [])];
                    const selectedPropertyText = allSelectedItems.map(item =>
                        `${item.name} (${item.uom}) - Min: ${item.minValue}, Max: ${item.maxValue}, Default: ${item.editableDefaultValue || item.defaultValue}`
                    ).join("; ");

                    return {
                        ...prev,
                        selectedProperties: updatedProperties,
                        selectedProperty: selectedPropertyText
                    }
                }
            })
        } else {
            setEditedComponentData((prev) => {
                const currentGroups = prev.selectedGroups || [];

                if (currentGroups.length === groupPropertyOptions.length) {
                    const allSelectedItems = [...(prev.selectedProperties || [])];
                    const selectedPropertyText = allSelectedItems.map(item =>
                        `${item.name} (${item.uom}) - Min: ${item.minValue}, Max: ${item.maxValue}, Default: ${item.editableDefaultValue || item.defaultValue}`
                    ).join("; ");

                    return {
                        ...prev,
                        selectedGroups: [],
                        selectedProperty: selectedPropertyText
                    }
                } else {
                    const updatedGroups = groupPropertyOptions.map(group => ({
                        ...group,
                        editableDefaultValue: group.defaultValue
                    }));
                    const allSelectedItems = [...(prev.selectedProperties || []), ...updatedGroups];
                    const selectedPropertyText = allSelectedItems.map(item =>
                        `${item.name} (${item.uom}) - Min: ${item.minValue}, Max: ${item.maxValue}, Default: ${item.editableDefaultValue || item.defaultValue}`
                    ).join("; ");

                    return {
                        ...prev,
                        selectedGroups: updatedGroups,
                        selectedProperty: selectedPropertyText
                    }
                }
            })
        }
    }

    const handleEditGroup = (group) => {
        setEditingGroup(group);
        setEditingGroupName(group.name); // Set current group name for editing
        setGroupComponentsToAdd([]);
        setShowEditGroupPopup(true);
    }

    // Function to update group name
    const handleUpdateGroupName = async () => {
        if (!editingGroup || !editingGroupName.trim()) {
            addNotification("error", "Please enter a valid group name");
            return;
        }

        try {
            const updateData = {
                ...editingGroup,
                name: editingGroupName.trim()
            };

            const result = await updateGroupReport(editingGroup._id, updateData);

            if (result.success) {
                addNotification("success", `Group name updated to "${editingGroupName.trim()}"`);
                await fetchGroupReports();
                setEditingGroup(updateData);
            }
        } catch (error) {
            console.error("Error updating group name:", error);
            addNotification("error", "Failed to update group name");
        }
    }

    const handleAddComponentsToGroup = async () => {
        if (!editingGroup || groupComponentsToAdd.length === 0) {
            addNotification("error", "Please select components to add");
            return;
        }

        try {
            const selectedComponentsData = groupComponentsToAdd
                .map((key) => {
                    const [type, index] = key.split("-")
                    const numericIndex = Number(index)
                    let component = null

                    if (type === "api") {
                        if (numericIndex >= 0 && numericIndex < apiComponents.length) {
                            component = apiComponents[numericIndex]
                        }
                    } else if (type === "added") {
                        if (numericIndex >= 0 && numericIndex < addedComponents.length) {
                            component = addedComponents[numericIndex]
                        }
                    }

                    if (!component) return null

                    const selectedPropertyNames = getSelectedPropertyNames(component);
                    const selectedPropertyText = selectedPropertyNames.join(", ");

                    return {
                        name: component.name,
                        volume: component.availableVolume || 1000,
                        cost: component.cost || 0,
                        gravity: component.gravity || 0,
                        costUnit: component.costUnit || currency,
                        properties: component.otherProductList || [],
                        selectedProperty: selectedPropertyText || "",
                        selectedProperties: component.selectedProperties || [],
                        selectedGroups: component.selectedGroups || [],
                        source: component.source || type
                    }
                })
                .filter(Boolean)

            if (selectedComponentsData.length === 0) {
                addNotification("error", "No valid components selected")
                return
            }

            const existingVolume = editingGroup.totalVolume || 0;
            const existingCost = editingGroup.totalCost || 0;

            const newComponentsVolume = selectedComponentsData.reduce((sum, comp) => sum + (comp.volume || 1000), 0);
            const newComponentsCost = selectedComponentsData.reduce((sum, comp) => sum + ((comp.cost || 0) * (comp.volume || 1000)), 0);

            const updatedComponents = [
                ...editingGroup.components,
                ...selectedComponentsData.map(comp => ({
                    ...comp,
                    percentage: ((comp.volume || 1000) / (existingVolume + newComponentsVolume)) * 100
                }))
            ];

            const totalVolume = existingVolume + newComponentsVolume;
            const updatedComponentsWithPercentages = updatedComponents.map(comp => ({
                ...comp,
                percentage: ((comp.volume || 1000) / totalVolume) * 100
            }));

            const updateData = {
                ...editingGroup,
                components: updatedComponentsWithPercentages,
                totalCost: existingCost + newComponentsCost,
                totalVolume: totalVolume
            };

            const result = await updateGroupReport(editingGroup._id, updateData);

            if (result.success) {
                addNotification("success", `Added ${selectedComponentsData.length} components to group "${editingGroup.name}"`);
                await fetchGroupReports();
                setEditingGroup(updateData);
                setGroupComponentsToAdd([]);
            }
        } catch (error) {
            console.error("Error adding components to group:", error);
            addNotification("error", `Failed to add components to group: ${error.message}`);
        }
    }

    const handleRemoveComponentFromGroup = async (componentIndex) => {
        if (!editingGroup) return;

        try {
            const updatedComponents = [...editingGroup.components];
            updatedComponents.splice(componentIndex, 1);

            const totalVolume = updatedComponents.reduce((sum, comp) => sum + (comp.volume || 1000), 0);
            const totalCost = updatedComponents.reduce((sum, comp) => sum + ((comp.cost || 0) * (comp.volume || 1000)), 0);

            const updatedComponentsWithPercentages = updatedComponents.map(comp => ({
                ...comp,
                percentage: ((comp.volume || 1000) / totalVolume) * 100
            }));

            const updateData = {
                ...editingGroup,
                components: updatedComponentsWithPercentages,
                totalCost: totalCost,
                totalVolume: totalVolume
            };

            const result = await updateGroupReport(editingGroup._id, updateData);

            if (result.success) {
                addNotification("success", "Component removed from group");
                await fetchGroupReports();
                setEditingGroup(updateData);
            }
        } catch (error) {
            console.error("Error removing component from group:", error);
            addNotification("error", "Failed to remove component from group");
        }
    }

    const handleCreateGroup = async () => {
        if (!groupName.trim()) {
            addNotification("error", "Please enter a group name")
            return
        }

        if (selectedComponents.length === 0) {
            addNotification("error", "Please select at least one component")
            return
        }

        const selectedComponentsData = selectedComponents
            .map((key) => {
                const [type, index] = key.split("-")
                const numericIndex = Number(index)
                let component = null

                if (type === "api") {
                    if (numericIndex >= 0 && numericIndex < apiComponents.length) {
                        component = apiComponents[numericIndex]
                    }
                } else if (type === "added") {
                    if (numericIndex >= 0 && numericIndex < addedComponents.length) {
                        component = addedComponents[numericIndex]
                    }
                }

                if (!component) return null

                const selectedPropertyNames = getSelectedPropertyNames(component);
                const selectedPropertyText = selectedPropertyNames.join(", ");

                return {
                    name: component.name,
                    volume: component.availableVolume || 1000,
                    cost: component.cost || 0,
                    gravity: component.gravity || 0,
                    costUnit: component.costUnit || currency,
                    properties: component.otherProductList || [],
                    selectedProperty: selectedPropertyText || "",
                    selectedProperties: component.selectedProperties || [],
                    selectedGroups: component.selectedGroups || [],
                    source: component.source || type
                }
            })
            .filter(Boolean)

        if (selectedComponentsData.length === 0) {
            addNotification("error", "No valid components selected")
            return
        }

        const totalVolume = selectedComponentsData.reduce((sum, comp) => sum + (comp.volume || 1000), 0);
        const totalCost = selectedComponentsData.reduce((sum, comp) => sum + ((comp.cost || 0) * (comp.volume || 1000)), 0);

        const groupReportData = {
            name: groupName.trim(),
            description: groupDescription.trim() || `Group report for ${groupName.trim()}`,
            components: selectedComponentsData.map(comp => ({
                ...comp,
                percentage: ((comp.volume || 1000) / totalVolume) * 100
            })),
            totalCost,
            totalVolume,
            currency,
            volumeUnit: localStorage.getItem("blendOptimizerVolumeUnit") || "bbl",
            createdBy: currentUser?.username || 'unknown'
        };

        try {
            const result = await createGroupReportFromGroup(groupReportData);

            if (result.success) {
                addNotification("success", `Group "${groupName}" created successfully`);

                const existingGroups = JSON.parse(localStorage.getItem('componentGroups') || '[]');
                const updatedGroups = [...existingGroups, {
                    ...groupReportData,
                    _id: result.data?._id || `local-${Date.now()}`,
                    id: result.data?._id || `local-${Date.now()}`,
                    createdAt: new Date().toISOString()
                }];
                localStorage.setItem('componentGroups', JSON.stringify(updatedGroups));

                window.dispatchEvent(new CustomEvent('componentGroupsUpdated', {
                    detail: { groups: updatedGroups }
                }));

                await fetchGroupReports();

                setShowAddGroupPopup(false);
                setSelectedComponents([]);
                setGroupName("");
                setGroupDescription("");

                setShowGroupReport(true);
                setShowGlobalReport(false);
            }
        } catch (error) {
            console.error("Error creating group:", error);
            addNotification("error", `Failed to create group: ${error.message}`);
        }
    }

    const handleDeleteGroupReport = async (reportId) => {
        if (window.confirm("Are you sure you want to delete this group report?")) {
            try {
                const result = await deleteGroupReport(reportId);

                if (result.success) {
                    const existingGroups = JSON.parse(localStorage.getItem('componentGroups') || '[]');
                    const updatedGroups = existingGroups.filter(group => group._id !== reportId && group.id !== reportId);
                    localStorage.setItem('componentGroups', JSON.stringify(updatedGroups));

                    window.dispatchEvent(new CustomEvent('componentGroupsUpdated', {
                        detail: { groups: updatedGroups }
                    }));

                    addNotification("success", "Group report deleted successfully");
                    await fetchGroupReports();
                }
            } catch (error) {
                console.error("Error deleting group report:", error);
                addNotification("error", "Failed to delete group report");
            }
        }
    };

    const toggleGroupReportExpansion = (reportId) => {
        setExpandedGroupReport(expandedGroupReport === reportId ? null : reportId)
    }

    //     return allComponents;
    // };

    const getUniqueComponents = () => {
    const allComponents = [];
    const seen = new Set();

    // Add API components (oldest first - already sorted)
    apiComponents.forEach((component, index) => {
        const key = `${component.name}-api`;
        if (!seen.has(key)) {
            seen.add(key);
            allComponents.push({
                ...component,
                source: component.source || 'api',
                sourceDisplay: component.source === 'excel-imported' ? 'Excel Imported' : 'API Component',
                isAddedComponent: false
            });
        }
    });

    // Add added components (oldest first - already sorted)
    addedComponents.forEach((component, index) => {
        const key = `${component.name}-added`;
        if (!seen.has(key)) {
            seen.add(key);
            allComponents.push({
                ...component,
                source: 'manual',
                sourceDisplay: 'Added Component',
                isAddedComponent: true
            });
        }
    });

    return allComponents;
};

    const downloadGlobalReportCSV = () => {
        const allComponents = getUniqueComponents();
        const exportTimestamp = new Date().toISOString();

        const headers = [
            "ComponentName","SourceType","Cost","Currency",
            "AvailabilityMin","AvailabilityMax","AvailabilityUOM",
            "SpecificGravity","PropertyCount","GroupCount","ExportTimestamp"
        ];

        const escapeCSV = (val) => {
            if (val === undefined || val === null) return "";
            const str = String(val);
            if (str.includes(",") || str.includes('"') || str.includes("\n")) {
                return '"' + str.replace(/"/g, '""') + '"';
            }
            return str;
        };

        const rows = allComponents.map((comp) => {
            const sourceType = comp.source === "api" ? "API" : comp.source === "excel-imported" ? "Excel" : "Manual";
            const propCount = (comp.selectedProperties || []).length;
            const groupCount = (comp.selectedGroups || []).length;
            return [
                escapeCSV(comp.name),
                escapeCSV(sourceType),
                comp.cost !== undefined && comp.cost !== null ? Number(comp.cost).toFixed(2) : "",
                escapeCSV(comp.costUnit || currency),
                comp.minVolume !== undefined && comp.minVolume !== null ? String(comp.minVolume) : "",
                comp.maxVolume !== undefined && comp.maxVolume !== null ? String(comp.maxVolume) : "",
                escapeCSV(comp.volumeUnit || "L"),
                comp.gravity !== undefined && comp.gravity !== null ? Number(comp.gravity).toFixed(4) : "",
                String(propCount),
                String(groupCount),
                exportTimestamp
            ];
        });

        const csvContent = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");
        const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `components_report_${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        addNotification("success", "Components report exported as CSV successfully");
    };

    const downloadGroupReportCSV = (report) => {
        const headers = ["Component Name", "Cost", "Gravity", "Selected Properties", "Volume", "Volume Unit", "Percentage", "Total Cost"]

        const rows = report.components.map((comp) => [
            comp.name || "",
            comp.cost || "",
            comp.gravity || "",
            comp.selectedProperty || "",
            comp.volume || "",
            report.volumeUnit || "",
            `${comp.percentage?.toFixed(2)}%` || "",
            `${report.currency}${((comp.cost || 0) * (comp.volume || 0)).toFixed(2)}`
        ])

        const csvContent = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n")

        const blob = new Blob([csvContent], { type: "text/csv" })
        const url = URL.revokeObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = `${report.name}_group_report.csv`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
    }

    const handleAddGroupClick = () => {
        setShowAddGroupPopup(true)
        setSelectedComponents([])
        setGroupName("")
        setGroupDescription("")
    }

    const handleComponentSelection = (componentKey) => {
        setSelectedComponents((prev) => {
            const isSelected = prev.includes(componentKey)
            if (isSelected) {
                return prev.filter((key) => key !== componentKey)
            } else {
                return [...prev, componentKey]
            }
        })
    }

    const handleGroupComponentSelection = (componentKey) => {
        setGroupComponentsToAdd((prev) => {
            const isSelected = prev.includes(componentKey)
            if (isSelected) {
                return prev.filter((key) => key !== componentKey)
            } else {
                return [...prev, componentKey]
            }
        })
    }

    const handleSelectAllComponents = () => {
        const allComponents = [...apiComponents, ...addedComponents]
        const allComponentKeys = allComponents.map((_, index) => {
            if (index < apiComponents.length) {
                return `api-${index}`
            } else {
                return `added-${index - apiComponents.length}`
            }
        })

        if (selectedComponents.length === allComponentKeys.length) {
            setSelectedComponents([])
        } else {
            setSelectedComponents(allComponentKeys)
        }
    }

    const handleSelectAllGroupComponents = () => {
        const allComponents = [...apiComponents, ...addedComponents]
        const allComponentKeys = allComponents.map((_, index) => {
            if (index < apiComponents.length) {
                return `api-${index}`
            } else {
                return `added-${index - apiComponents.length}`
            }
        })

        if (groupComponentsToAdd.length === allComponentKeys.length) {
            setGroupComponentsToAdd([])
        } else {
            setGroupComponentsToAdd(allComponentKeys)
        }
    }

    const handlePreviewComponentSelection = (componentId) => {
        setSelectedPreviewComponents(prev => {
            if (prev.includes(componentId)) {
                return prev.filter(id => id !== componentId);
            } else {
                return [...prev, componentId];
            }
        });
    };

    const handleSelectAllPreviewComponents = () => {
        if (selectedPreviewComponents.length === excelPreviewData.length) {
            setSelectedPreviewComponents([]);
        } else {
            setSelectedPreviewComponents(excelPreviewData.map(comp => comp.id));
        }
    };

    const applyFilters = (components) => {
        return components.filter((component) => {
            if (searchTerm) {
                const searchLower = searchTerm.toLowerCase()
                const matchesSearch =
                    component.name.toLowerCase().includes(searchLower) ||
                    (component.sourceSet && component.sourceSet.toLowerCase().includes(searchLower)) ||
                    (component.selectedProperty && component.selectedProperty.toLowerCase().includes(searchLower))
                if (!matchesSearch) return false
            }

            // Source filter
            if (sourceFilter !== "all") {
                if (sourceFilter === "api" && component.source !== "api") return false
                if (sourceFilter === "added" && component.source !== "manual") return false
                if (sourceFilter === "excel" && component.source !== "excel-imported") return false
            }

            const cost = Number(component.cost) || 0
            const gravity = Number(component.gravity) || 0

            if (filters.minCost && cost < Number(filters.minCost)) return false
            if (filters.maxCost && cost > Number(filters.maxCost)) return false
            if (filters.minGravity && gravity < Number(filters.minGravity)) return false
            if (filters.maxGravity && gravity > Number(filters.maxGravity)) return false

            return true
        })
    }

    const uniqueComponents = getUniqueComponents()
    const filteredUniqueComponents = applyFilters(uniqueComponents)

    filteredUniqueComponents.sort((a, b) => {
        if (sortBy === "newest") return new Date(b.createdAt || b.timestamp) - new Date(a.createdAt || a.timestamp)
        if (sortBy === "oldest") return new Date(a.createdAt || a.timestamp) - new Date(b.createdAt || b.timestamp)
        if (sortBy === "name") return (a.name || "").localeCompare(b.name || "")
        if (sortBy === "cost") return (Number(b.cost) || 0) - (Number(a.cost) || 0)
        if (sortBy === "availability") return (Number(b.availableVolume) || 0) - (Number(a.availableVolume) || 0)
        if (sortBy === "gravity") return (Number(b.gravity) || 0) - (Number(a.gravity) || 0)
        return 0
    })

    const totalPages = Math.ceil(filteredUniqueComponents.length / itemsPerPage)
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const currentGlobalItems = filteredUniqueComponents.slice(startIndex, endIndex)

    const handlePageChange = (page) => {
        setCurrentPage(page)
        window.scrollTo({ top: 0, behavior: "smooth" })
    }

    const getPageNumbers = () => {
        const pageNumbers = []
        const maxVisiblePages = 5

        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i)
            }
        } else {
            const startPage = Math.max(1, currentPage - 2)
            const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

            for (let i = startPage; i <= endPage; i++) {
                pageNumbers.push(i)
            }
        }

        return pageNumbers
    }

    return (
        <div className="min-h-screen bg-gray-50 flex">
            <NotificationToaster notifications={notifications} />

            <LogoutConfirmModal 
                isOpen={showLogoutConfirm}
                onConfirm={handleConfirmLogout}
                onCancel={handleCancelLogout}
            />

            {/* ─── Bulk Delete Confirmation Modal ─── */}
            {showBulkDeleteConfirm && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-lg max-w-sm w-full">
                        <div className="p-5">
                            <div className="flex items-start gap-3 mb-4">
                                <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                                    <IconTrash className="w-5 h-5 text-red-600" />
                                </div>
                                <div>
                                    <h3 className="text-base font-semibold text-gray-900">Delete Selected Components</h3>
                                    <p className="text-gray-500 text-sm mt-1">
                                        Are you sure you want to delete {selectedForBulkDelete.length} selected component(s)? This action cannot be undone.
                                    </p>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <BtnOutline onClick={() => setShowBulkDeleteConfirm(false)} className="flex-1 justify-center">Cancel</BtnOutline>
                                <BtnDanger onClick={handleBulkDeleteConfirm} className="flex-1 justify-center">
                                    <IconTrash className="w-4 h-4" /> Delete ({selectedForBulkDelete.length})
                                </BtnDanger>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Delete Single Confirmation Modal ─── */}
            {showDeleteConfirm && componentToDelete && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-lg max-w-sm w-full">
                        <div className="p-5">
                            <div className="flex items-start gap-3 mb-4">
                                <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                                    <IconTrash className="w-5 h-5 text-red-600" />
                                </div>
                                <div>
                                    <h3 className="text-base font-semibold text-gray-900">Delete Component</h3>
                                    <p className="text-gray-500 text-sm mt-1">
                                        Are you sure you want to delete "{componentToDelete.name}"? This action cannot be undone.
                                    </p>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <BtnOutline onClick={handleDeleteCancel} className="flex-1 justify-center">Cancel</BtnOutline>
                                <BtnDanger onClick={handleDeleteConfirm} className="flex-1 justify-center">
                                    <IconTrash className="w-4 h-4" /> Delete
                                </BtnDanger>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Edit Group Modal ─── */}
            {showEditGroupPopup && editingGroup && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-5 max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-xl">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center">
                                    <IconEdit className="w-4 h-4 text-purple-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Edit Group: {editingGroup.name}</h3>
                            </div>
                            <button onClick={() => { setShowEditGroupPopup(false); setEditingGroup(null); setGroupComponentsToAdd([]); setEditingGroupName(""); }} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                                <IconClose className="w-5 h-5 text-gray-400" />
                            </button>
                        </div>

                        <div className="mb-5 bg-blue-50 p-4 rounded-lg border border-blue-100">
                            <h4 className="text-sm font-medium text-blue-900 mb-2">Update Group Name</h4>
                            <div className="flex gap-2">
                                <input type="text" value={editingGroupName} onChange={(e) => setEditingGroupName(e.target.value)} placeholder="Enter new group name" className="flex-1 px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                                <BtnPrimary onClick={handleUpdateGroupName}><IconCheck className="w-4 h-4" /> Update</BtnPrimary>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 flex-1 overflow-hidden">
                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 overflow-hidden flex flex-col">
                                <h4 className="text-sm font-medium text-gray-900 mb-3">Current Components ({editingGroup.components.length})</h4>
                                <div className="flex-1 overflow-y-auto space-y-2">
                                    {editingGroup.components.map((component, index) => (
                                        <div key={index} className="bg-white rounded-lg border border-gray-200 p-3 flex justify-between items-center">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex justify-between items-center">
                                                    <span className="font-medium text-gray-900 text-sm truncate">{component.name}</span>
                                                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded ml-2 flex-shrink-0">{component.percentage?.toFixed(1)}%</span>
                                                </div>
                                                <div className="text-xs text-gray-500 mt-1">
                                                    Cost: {editingGroup.currency}{component.cost.toFixed(2)} · SG: {component.gravity} · Vol: {component.volume} {editingGroup.volumeUnit}
                                                </div>
                                            </div>
                                            <button onClick={() => handleRemoveComponentFromGroup(index)} className="ml-3 p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0" title="Remove from group">
                                                <IconTrash className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-3 pt-3 border-t border-gray-200 space-y-1 text-sm">
                                    <div className="flex justify-between"><span className="text-gray-500">Total Components:</span><span className="font-medium">{editingGroup.components.length}</span></div>
                                    <div className="flex justify-between"><span className="text-gray-500">Total Volume:</span><span className="font-medium">{editingGroup.totalVolume} {editingGroup.volumeUnit}</span></div>
                                    <div className="flex justify-between"><span className="text-gray-500">Total Cost:</span><span className="font-medium">{editingGroup.currency}{editingGroup.totalCost.toFixed(2)}</span></div>
                                </div>
                            </div>

                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 overflow-hidden flex flex-col">
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-sm font-medium text-gray-900">Add Components ({groupComponentsToAdd.length} selected)</h4>
                                    <button onClick={handleSelectAllGroupComponents} className="text-xs font-medium text-blue-600 hover:text-blue-800">
                                        {groupComponentsToAdd.length === (apiComponents.length + addedComponents.length) ? "Deselect All" : "Select All"}
                                    </button>
                                </div>
                                <div className="flex-1 overflow-y-auto space-y-2">
                                    {apiComponents.map((component, index) => {
                                        const componentKey = `api-${index}`;
                                        const isSelected = groupComponentsToAdd.includes(componentKey);
                                        return (
                                            <div key={generateUniqueKey(component, index, 'api')} className={`bg-white rounded-lg border p-3 cursor-pointer transition-colors ${isSelected ? "border-blue-400 bg-blue-50" : "border-gray-200 hover:bg-gray-50"}`} onClick={() => handleGroupComponentSelection(componentKey)}>
                                                <div className="flex items-center gap-2">
                                                    <input type="checkbox" checked={isSelected} onChange={() => handleGroupComponentSelection(componentKey)} className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 flex-shrink-0" onClick={(e) => e.stopPropagation()} />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex justify-between items-center">
                                                            <span className="font-medium text-gray-900 text-sm truncate">{component.name}</span>
                                                            <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded ml-2">API</span>
                                                        </div>
                                                        <div className="text-xs text-gray-500 mt-0.5">
                                                            {getCurrencySymbol(component.costUnit)}{component.cost.toFixed(2)} · SG: {component.gravity}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {addedComponents.map((component, index) => {
                                        const componentKey = `added-${index}`;
                                        const isSelected = groupComponentsToAdd.includes(componentKey);
                                        return (
                                            <div key={generateUniqueKey(component, index, 'added')} className={`bg-white rounded-lg border p-3 cursor-pointer transition-colors ${isSelected ? "border-blue-400 bg-blue-50" : "border-gray-200 hover:bg-gray-50"}`} onClick={() => handleGroupComponentSelection(componentKey)}>
                                                <div className="flex items-center gap-2">
                                                    <input type="checkbox" checked={isSelected} onChange={() => handleGroupComponentSelection(componentKey)} className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 flex-shrink-0" onClick={(e) => e.stopPropagation()} />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex justify-between items-center">
                                                            <span className="font-medium text-gray-900 text-sm truncate">{component.name}</span>
                                                            <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded ml-2">Added</span>
                                                        </div>
                                                        <div className="text-xs text-gray-500 mt-0.5">
                                                            {getCurrencySymbol(component.costUnit)}{component.cost.toFixed(2)} · SG: {component.gravity}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="mt-3 pt-3 border-t border-gray-200">
                                    <BtnPrimary onClick={handleAddComponentsToGroup} disabled={groupComponentsToAdd.length === 0} className="w-full justify-center">
                                        <IconPlus className="w-4 h-4" /> Add Selected ({groupComponentsToAdd.length})
                                    </BtnPrimary>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Import (Excel) Modal ─── */}
            {showExcelImportPopup && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-5 max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-xl">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center">
                                    <IconUpload className="w-4 h-4 text-emerald-600" />
                                </div>
                                <div>
                                    <h3 className="text-base font-semibold text-gray-900">Import (Excel)</h3>
                                    <p className="text-xs text-gray-500">Upload an Excel file to import components</p>
                                </div>
                            </div>
                            <button onClick={() => { setShowExcelImportPopup(false); setShowExcelPreview(false); setExcelPreviewData([]); setSelectedPreviewComponents([]); setFile(null); }} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                                <IconClose className="w-5 h-5 text-gray-400" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center hover:border-blue-400 transition-all bg-gray-50 hover:bg-blue-50/30">
                                <IconUpload className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                                <div className="flex text-sm text-gray-600 justify-center">
                                    <label htmlFor="excel-file-components-popup" className="relative cursor-pointer rounded-lg font-medium text-blue-600 hover:text-blue-500 bg-white px-4 py-2 border border-blue-200 hover:border-blue-300 transition-colors">
                                        <span>Upload Excel file</span>
                                        <input id="excel-file-components-popup" name="excel-file-components-popup" type="file" accept=".xlsx, .xls" className="sr-only" onChange={onChooseFile} />
                                    </label>
                                </div>
                                <p className="text-xs text-gray-400 mt-2">.XLSX or .XLS files only, up to 10MB</p>
                                {file && (
                                    <div className="mt-3 p-2 bg-white rounded-lg border border-green-200 inline-flex items-center gap-2">
                                        <IconCheck className="w-3.5 h-3.5 text-green-500" />
                                        <span className="text-xs font-medium text-gray-700 truncate max-w-[200px]">{file.name}</span>
                                        <span className="text-xs text-gray-400">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                                    </div>
                                )}
                            </div>

                            {file && !showExcelPreview && (
                                <div className="flex justify-center">
                                    <BtnPrimary onClick={loadExcelForPreview} disabled={loading}>
                                        {loading ? <><IconSpinner className="w-4 h-4" /> Loading...</> : <>Load Excel Data</>}
                                    </BtnPrimary>
                                </div>
                            )}

                            {showExcelPreview && excelPreviewData.length > 0 && (
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="text-sm font-medium text-gray-900">Preview ({selectedPreviewComponents.length}/{excelPreviewData.length} selected)</h4>
                                        <button onClick={handleSelectAllPreviewComponents} className="text-xs font-medium text-blue-600 hover:text-blue-800">
                                            {selectedPreviewComponents.length === excelPreviewData.length ? "Deselect All" : "Select All"}
                                        </button>
                                    </div>
                                    <div className="overflow-x-auto max-h-60">
                                        <table className="min-w-full text-xs">
                                            <thead className="bg-gray-100 sticky top-0">
                                                <tr>
                                                    <th className="w-8 px-2 py-2"><input type="checkbox" className="w-3 h-3" checked={selectedPreviewComponents.length === excelPreviewData.length} onChange={handleSelectAllPreviewComponents} /></th>
                                                    <th className="px-2 py-2 text-left font-medium text-gray-600 uppercase">Component</th>
                                                    <th className="px-2 py-2 text-right font-medium text-gray-600 uppercase">Cost</th>
                                                    <th className="px-2 py-2 text-left font-medium text-gray-600 uppercase">Currency</th>
                                                    <th className="px-2 py-2 text-right font-medium text-gray-600 uppercase">Sp. Gravity</th>
                                                    <th className="px-2 py-2 text-right font-medium text-gray-600 uppercase">Min Vol</th>
                                                    <th className="px-2 py-2 text-right font-medium text-gray-600 uppercase">Max Vol</th>
                                                    <th className="px-2 py-2 text-right font-medium text-gray-600 uppercase">Avail. Vol</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200 bg-white">
                                                {excelPreviewData.map((component) => (
                                                    <tr key={component.id} className={`hover:bg-gray-50 ${selectedPreviewComponents.includes(component.id) ? 'bg-blue-50' : ''}`}>
                                                        <td className="px-2 py-2"><input type="checkbox" checked={selectedPreviewComponents.includes(component.id)} onChange={() => handlePreviewComponentSelection(component.id)} className="w-3 h-3 text-blue-600 rounded" /></td>
                                                        <td className="px-2 py-2 font-medium text-gray-900 truncate max-w-[120px]">{component.name}</td>
                                                        <td className="px-2 py-2 text-right text-gray-900 font-mono">{getCurrencySymbol(component.costUnit)}{component.cost.toFixed(2)}</td>
                                                        <td className="px-2 py-2 text-gray-600">{component.costUnit}</td>
                                                        <td className="px-2 py-2 text-right text-gray-600 font-mono">{component.gravity}</td>
                                                        <td className="px-2 py-2 text-right text-gray-600">{component.minVolume}</td>
                                                        <td className="px-2 py-2 text-right text-gray-600">{component.maxVolume}</td>
                                                        <td className="px-2 py-2 text-right text-gray-600">{component.availableVolume}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2">{selectedPreviewComponents.length} of {excelPreviewData.length} components selected for import.</p>
                                </div>
                            )}

                            <div className="flex gap-2 justify-end pt-2 border-t border-gray-100">
                                <BtnOutline onClick={() => { setShowExcelImportPopup(false); setShowExcelPreview(false); setExcelPreviewData([]); setSelectedPreviewComponents([]); setFile(null); }}>Cancel</BtnOutline>
                                {showExcelPreview && (
                                    <BtnPrimary onClick={importPreviewedComponents} disabled={loading || selectedPreviewComponents.length === 0}>
                                        {loading ? <><IconSpinner className="w-4 h-4" /> Importing...</> : <>Import Selected ({selectedPreviewComponents.length})</>}
                                    </BtnPrimary>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Add Component Modal ─── */}
            {showAddComponentModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-xl">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
                                    <IconBox className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-base font-semibold text-gray-900">Add Component</h3>
                                    <p className="text-xs text-gray-500">Define component properties and quality parameters</p>
                                </div>
                            </div>
                            <button onClick={() => setShowAddComponentModal(false)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                                <IconClose className="w-5 h-5 text-gray-400" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                            {/* Basic Info */}
                            <div>
                                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Basic Info</h4>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Component Name <span className="text-red-500">*</span></label>
                                    <input type="text" value={newComponent.name} onChange={(e) => setNewComponent({ ...newComponent, name: e.target.value })} placeholder="e.g. Naphtha, FCC Gasoline, MTBE" className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm placeholder-gray-400" />
                                </div>
                            </div>

                            {/* Economics */}
                            <div>
                                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Economics</h4>
                                <div className="grid grid-cols-3 gap-3">
                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Unit Cost</label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{currencies.find(c => c.code === newComponent.costUnit)?.symbol || ''}</span>
                                            <input type="number" step="0.01" value={newComponent.cost} onChange={(e) => setNewComponent({ ...newComponent, cost: e.target.value })} placeholder="0.00" className="w-full pl-7 pr-3 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Currency</label>
                                        <select value={newComponent.costUnit} onChange={(e) => setNewComponent({ ...newComponent, costUnit: e.target.value })} className="w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                                            {currencies.map((curr) => (<option key={curr.code} value={curr.code}>{curr.symbol} {curr.code}</option>))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Quantity / Availability */}
                            <div>
                                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Quantity / Availability</h4>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Min Volume</label>
                                        <input type="number" value={newComponent.minVolume} onChange={(e) => setNewComponent({ ...newComponent, minVolume: e.target.value })} placeholder="0" className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Max Volume</label>
                                        <input type="number" value={newComponent.maxVolume} onChange={(e) => setNewComponent({ ...newComponent, maxVolume: e.target.value })} placeholder="1000" className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Available</label>
                                        <input type="number" value={newComponent.availableVolume} onChange={(e) => setNewComponent({ ...newComponent, availableVolume: e.target.value })} placeholder="500" className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">UOM</label>
                                        <select value={newComponent.volumeUnit} onChange={(e) => setNewComponent({ ...newComponent, volumeUnit: e.target.value })} className="w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                                            <option value="L">Liters (L)</option><option value="gal">Gallons</option><option value="bbl">Barrels</option><option value="m3">m³</option><option value="kg">kg</option><option value="MT">MT</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Quality */}
                            <div>
                                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Quality</h4>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Specific Gravity</label>
                                    <input type="number" step="0.0001" min="0.5" max="2" value={newComponent.gravity} onChange={(e) => setNewComponent({ ...newComponent, gravity: e.target.value })} placeholder="e.g. 0.7530" className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                                </div>
                            </div>

                            {/* Selected Properties Preview */}
                            {(newComponent.selectedProperties.length > 0 || newComponent.selectedGroups.length > 0) && (
                                <div>
                                    <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Assigned Properties ({newComponent.selectedProperties.length + newComponent.selectedGroups.length})</h4>
                                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                                        <table className="w-full text-sm">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Property</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">UOM</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                                                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Range</th>
                                                    <th className="w-8"></th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {[...newComponent.selectedProperties.map(p => ({...p, _type:'global'})), ...newComponent.selectedGroups.map(p => ({...p, _type:'group'}))].map((item, index) => (
                                                    <tr key={index} className="hover:bg-gray-50">
                                                        <td className="px-3 py-2"><div className="flex items-center gap-1.5"><span className={`w-1.5 h-1.5 rounded-full ${item._type==='global' ? 'bg-blue-500' : 'bg-purple-500'}`} /><span className="font-medium text-gray-800">{item.name}</span></div></td>
                                                        <td className="px-3 py-2 text-gray-500 text-xs">{item.uom}</td>
                                                        <td className="px-3 py-2"><input type="number" value={item.editableDefaultValue !== undefined ? item.editableDefaultValue : item.defaultValue} onChange={(e) => handleDefaultValueChange(item.name, e.target.value, item._type === 'global')} className="w-20 px-2 py-1 border border-gray-300 rounded-lg text-xs text-center focus:ring-2 focus:ring-blue-500" step="0.1" /></td>
                                                        <td className="px-3 py-2 text-center"><span className="text-xs text-gray-400">{item.minValue}–{item.maxValue}</span></td>
                                                        <td className="px-2 py-2"><button type="button" onClick={() => handlePropertySelection(item)} className="p-1 text-gray-300 hover:text-red-500 transition-colors"><IconClose className="w-4 h-4" /></button></td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Property Selection */}
                            <div>
                                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Quality Properties</h4>
                                <div className="flex bg-gray-100 rounded-lg p-1 gap-1 mb-4">
                                    <button type="button" onClick={() => setPropertyType("global")} className={`flex-1 py-2 rounded-md text-sm font-medium transition-all ${propertyType === "global" ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>Global Properties</button>
                                    <button type="button" onClick={() => setPropertyType("group")} className={`flex-1 py-2 rounded-md text-sm font-medium transition-all ${propertyType === "group" ? "bg-white text-purple-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>Group Properties</button>
                                </div>
                                {(propertyType === "global" ? globalPropertyOptions : groupPropertyOptions).length > 0 ? (
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-xs text-gray-500">{(propertyType === "global" ? globalPropertyOptions : groupPropertyOptions).length} available · {propertyType === "global" ? newComponent.selectedProperties.length : newComponent.selectedGroups.length} selected</span>
                                            <button type="button" onClick={handleSelectAllProperties} className="text-xs font-medium text-blue-600 hover:text-blue-800">
                                                {propertyType === "global" ? (newComponent.selectedProperties.length === globalPropertyOptions.length ? "Deselect All" : "Select All") : (newComponent.selectedGroups.length === groupPropertyOptions.length ? "Deselect All" : "Select All")}
                                            </button>
                                        </div>
                                        <div className="border border-gray-200 rounded-lg max-h-44 overflow-y-auto divide-y divide-gray-100">
                                            {(propertyType === "global" ? globalPropertyOptions : groupPropertyOptions).map((item, index) => {
                                                const isSelected = propertyType === "global" ? newComponent.selectedProperties.some(p => p.name === item.name) : newComponent.selectedGroups.some(g => g.name === item.name);
                                                return (
                                                    <div key={`${propertyType}-${index}-${item.name}`} className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`} onClick={() => handlePropertySelection(item)}>
                                                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                                                            {isSelected && <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                                                        </div>
                                                        <span className="flex-1 font-medium text-gray-900 text-sm">{item.displayName}</span>
                                                        <div className="flex items-center gap-3 text-xs text-gray-400">
                                                            <span>Min {item.minValue}</span><span>Max {item.maxValue}</span>
                                                            <span className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-500">{item.uom || 'NA'}</span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-8 text-gray-400 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                                        <IconClipboard className="w-8 h-8 mb-2 text-gray-300" />
                                        <p className="text-sm">No {propertyType} properties available</p>
                                        <p className="text-xs mt-1">Add {propertyType} properties in the Properties page first</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/50 rounded-b-xl">
                            <div className="text-sm text-gray-500">
                                {(newComponent.selectedProperties.length + newComponent.selectedGroups.length > 0) && (
                                    <span className="inline-flex items-center gap-1.5 text-blue-600 font-medium">
                                        <IconCheck className="w-4 h-4" /> {newComponent.selectedProperties.length + newComponent.selectedGroups.length} properties assigned
                                    </span>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <BtnOutline onClick={() => setShowAddComponentModal(false)}>Cancel</BtnOutline>
                                <BtnPrimary onClick={handleAddComponent} disabled={!newComponent.name.trim() || (newComponent.selectedProperties.length === 0 && newComponent.selectedGroups.length === 0)}>
                                    <IconPlus className="w-4 h-4" /> Save Component
                                </BtnPrimary>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Edit Component Modal ─── */}
            {showEditComponentPopup && editingComponent && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-5 max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-xl">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <IconEdit className="w-4 h-4 text-blue-600" />
                                </div>
                                <h3 className="text-base font-semibold text-gray-900">Edit Component</h3>
                            </div>
                            <button onClick={handleEditComponentCancel} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                                <IconClose className="w-5 h-5 text-gray-400" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Component Name <span className="text-red-500">*</span></label>
                                <input type="text" value={editedComponentData.name || ""} onChange={(e) => handleComponentFieldChange("name", e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Cost</label>
                                    <input type="number" step="0.01" value={editedComponentData.cost || ""} onChange={(e) => handleComponentFieldChange("cost", e.target.value)} placeholder="0.00" className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                                    <select value={editedComponentData.costUnit || "INR"} onChange={(e) => handleComponentFieldChange("costUnit", e.target.value)} className="w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                                        {currencies.map((curr) => (<option key={curr.code} value={curr.code}>{curr.symbol} {curr.code}</option>))}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Specific Gravity</label>
                                <input type="number" step="0.01" value={editedComponentData.gravity || ""} onChange={(e) => handleComponentFieldChange("gravity", e.target.value)} placeholder="0.00" className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                            </div>

                            {/* Selected Global Properties */}
                            {(editedComponentData.selectedProperties || []).length > 0 && (
                                <div className="border rounded-lg p-3 bg-blue-50 border-blue-200">
                                    <h4 className="text-xs font-medium mb-2 text-blue-900">Global Properties:</h4>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full text-xs border rounded border-blue-200">
                                            <thead className="bg-blue-100"><tr><th className="px-2 py-1 text-left font-medium text-blue-900">Property</th><th className="px-2 py-1 text-left font-medium text-blue-900">UOM</th><th className="px-2 py-1 text-left font-medium text-blue-900">Value</th><th className="px-2 py-1 text-left font-medium text-blue-900">Action</th></tr></thead>
                                            <tbody>
                                                {(editedComponentData.selectedProperties || []).map((item, index) => (
                                                    <tr key={`edit-global-${index}-${item.name}`} className="border-b border-blue-200 last:border-b-0">
                                                        <td className="px-2 py-1 text-blue-800">{item.name}</td>
                                                        <td className="px-2 py-1 text-blue-700">{item.uom}</td>
                                                        <td className="px-2 py-1"><input type="number" value={item.editableDefaultValue || item.defaultValue} onChange={(e) => handleEditDefaultValueChange(item.name, e.target.value, true)} className="w-16 px-1 py-0.5 border border-gray-300 rounded text-xs" min={item.minValue} max={item.maxValue} step="0.1" /></td>
                                                        <td className="px-2 py-1"><button type="button" onClick={() => handleEditPropertySelection(item)} className="text-red-500 hover:text-red-700 text-xs font-medium">Remove</button></td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Selected Group Properties */}
                            {(editedComponentData.selectedGroups || []).length > 0 && (
                                <div className="border rounded-lg p-3 bg-purple-50 border-purple-200">
                                    <h4 className="text-xs font-medium mb-2 text-purple-900">Group Properties:</h4>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full text-xs border rounded border-purple-200">
                                            <thead className="bg-purple-100"><tr><th className="px-2 py-1 text-left font-medium text-purple-900">Group</th><th className="px-2 py-1 text-left font-medium text-purple-900">UOM</th><th className="px-2 py-1 text-left font-medium text-purple-900">Value</th><th className="px-2 py-1 text-left font-medium text-purple-900">Action</th></tr></thead>
                                            <tbody>
                                                {(editedComponentData.selectedGroups || []).map((item, index) => (
                                                    <tr key={`edit-group-${index}-${item.name}`} className="border-b border-purple-200 last:border-b-0">
                                                        <td className="px-2 py-1 text-purple-800">{item.name}</td>
                                                        <td className="px-2 py-1 text-purple-700">{item.uom}</td>
                                                        <td className="px-2 py-1"><input type="number" value={item.editableDefaultValue || item.defaultValue} onChange={(e) => handleEditDefaultValueChange(item.name, e.target.value, false)} className="w-16 px-1 py-0.5 border border-gray-300 rounded text-xs" min={item.minValue} max={item.maxValue} step="0.1" /></td>
                                                        <td className="px-2 py-1"><button type="button" onClick={() => handleEditPropertySelection(item)} className="text-red-500 hover:text-red-700 text-xs font-medium">Remove</button></td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Property Type Toggle */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Property Type <span className="text-red-500">*</span></label>
                                <div className="grid grid-cols-2 gap-2">
                                    <button type="button" onClick={() => setPropertyType("global")} className={`px-4 py-2 rounded-lg font-medium transition-colors text-sm ${propertyType === "global" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200"}`}>Global Properties</button>
                                    <button type="button" onClick={() => setPropertyType("group")} className={`px-4 py-2 rounded-lg font-medium transition-colors text-sm ${propertyType === "group" ? "bg-purple-600 text-white" : "bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200"}`}>Group Properties</button>
                                </div>
                            </div>

                            {/* Property Selection List */}
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <label className="text-sm font-medium text-gray-700">{propertyType === "global" ? "Global" : "Group"} Properties <span className="text-gray-400">({propertyType === "global" ? (editedComponentData.selectedProperties || []).length : (editedComponentData.selectedGroups || []).length} selected)</span></label>
                                    <button type="button" onClick={handleEditSelectAllProperties} className="text-xs font-medium text-blue-600 hover:text-blue-800">
                                        {propertyType === "global" ? ((editedComponentData.selectedProperties || []).length === globalPropertyOptions.length ? "Deselect All" : "Select All") : ((editedComponentData.selectedGroups || []).length === groupPropertyOptions.length ? "Deselect All" : "Select All")}
                                    </button>
                                </div>
                                {(propertyType === "global" ? globalPropertyOptions : groupPropertyOptions).length > 0 ? (
                                    <div className="border border-gray-300 rounded-lg max-h-48 overflow-y-auto divide-y divide-gray-200">
                                        {(propertyType === "global" ? globalPropertyOptions : groupPropertyOptions).map((item, index) => {
                                            const currentItems = propertyType === "global" ? (editedComponentData.selectedProperties || []) : (editedComponentData.selectedGroups || []);
                                            const isSelected = currentItems.some(p => p.name === item.name);
                                            return (
                                                <div key={`edit-${propertyType}-prop-${index}-${item.name}`} className={`p-2.5 cursor-pointer transition-colors ${isSelected ? (propertyType === "global" ? "bg-blue-50 border-l-4 border-l-blue-500" : "bg-purple-50 border-l-4 border-l-purple-500") : "hover:bg-gray-50"}`} onClick={() => handleEditPropertySelection(item)}>
                                                    <div className="flex items-center gap-2">
                                                        <div className={`w-4 h-4 rounded border flex items-center justify-center ${isSelected ? (propertyType === "global" ? "bg-blue-500 border-blue-500" : "bg-purple-500 border-purple-500") : "border-gray-300"}`}>
                                                            {isSelected && <svg className="w-2 h-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>}
                                                        </div>
                                                        <div className="flex-1">
                                                            <span className="font-medium text-gray-900 text-sm">{item.displayName}</span>
                                                            <div className="text-xs text-gray-500 mt-0.5">Min: {item.minValue} · Max: {item.maxValue} · Default: {item.defaultValue}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg border border-gray-200 text-xs">No {propertyType} properties available</div>
                                )}
                            </div>

                            {/* Summary */}
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                <p className="text-xs text-amber-700">
                                    Total selected: <span className="font-semibold">{(editedComponentData.selectedProperties || []).length + (editedComponentData.selectedGroups || []).length}</span> properties
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-2 justify-end pt-4 border-t border-gray-100 mt-4">
                            <BtnOutline onClick={handleEditComponentCancel}>Cancel</BtnOutline>
                            <BtnPrimary onClick={handleEditComponentSave}><IconCheck className="w-4 h-4" /> Save Changes</BtnPrimary>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Create Group Modal ─── */}
            {showAddGroupPopup && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-5 max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-xl">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 bg-indigo-100 rounded-lg flex items-center justify-center">
                                    <IconGroup className="w-4 h-4 text-indigo-600" />
                                </div>
                                <div>
                                    <h3 className="text-base font-semibold text-gray-900">Create Group</h3>
                                    <p className="text-xs text-gray-500">Organize components into a named group</p>
                                </div>
                            </div>
                            <button onClick={() => setShowAddGroupPopup(false)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                                <IconClose className="w-5 h-5 text-gray-400" />
                            </button>
                        </div>

                        {/* Step 1: Group Details */}
                        <div className="mb-4 space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Group Name <span className="text-red-500">*</span></label>
                                <input type="text" value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Enter group name" className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                                <input type="text" value={groupDescription} onChange={(e) => setGroupDescription(e.target.value)} placeholder="Brief description of this group" className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                            </div>
                        </div>

                        {/* Selected Panel */}
                        {selectedComponents.length > 0 && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="text-sm font-medium text-blue-900">Selected Components ({selectedComponents.length})</h4>
                                    <button onClick={() => setSelectedComponents([])} className="text-xs font-medium text-blue-600 hover:text-blue-800">Clear</button>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    {selectedComponents.slice(0, 8).map((componentKey) => {
                                        const [type, index] = componentKey.split("-");
                                        const numericIndex = Number(index);
                                        let component = null;
                                        if (type === "api" && numericIndex >= 0 && numericIndex < apiComponents.length) component = apiComponents[numericIndex];
                                        else if (type === "added" && numericIndex >= 0 && numericIndex < addedComponents.length) component = addedComponents[numericIndex];
                                        if (!component) return null;
                                        return (
                                            <span key={`selected-${componentKey}`} className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1">
                                                <span className="truncate max-w-[100px]">{component.name}</span>
                                                <button onClick={() => handleComponentSelection(componentKey)} className="text-blue-600 hover:text-blue-800">×</button>
                                            </span>
                                        );
                                    })}
                                    {selectedComponents.length > 8 && <span className="bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full text-xs font-medium">+{selectedComponents.length - 8} more</span>}
                                </div>
                            </div>
                        )}

                        {/* Step 2: Select Components */}
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-medium text-gray-900">Select Components</h4>
                            <div className="flex items-center gap-2">
                                <button onClick={handleSelectAllComponents} className="text-xs font-medium text-blue-600 hover:text-blue-800">
                                    {selectedComponents.length === (apiComponents.length + addedComponents.length) ? "Deselect All" : "Select All"}
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg">
                            <table className="min-w-full text-xs">
                                <thead className="bg-gray-50 sticky top-0">
                                    <tr>
                                        <th className="px-2 py-2 text-left w-8">Select</th>
                                        <th className="px-2 py-2 text-left font-medium text-gray-600 uppercase">Component</th>
                                        <th className="px-2 py-2 text-right font-medium text-gray-600 uppercase">Cost ({getCurrencySymbol()})</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 bg-white">
                                    {apiComponents.map((component, index) => {
                                        const componentKey = `api-${index}`;
                                        const isSelected = selectedComponents.includes(componentKey);
                                        return (
                                            <tr key={generateUniqueKey(component, index, 'api')} className={`hover:bg-gray-50 cursor-pointer ${isSelected ? "bg-blue-50" : ""}`} onClick={() => handleComponentSelection(componentKey)}>
                                                <td className="px-2 py-2"><input type="checkbox" checked={isSelected} onChange={() => handleComponentSelection(componentKey)} className="w-3.5 h-3.5 text-blue-600 rounded" onClick={(e) => e.stopPropagation()} /></td>
                                                <td className="px-2 py-2 font-medium text-gray-900">{component.name}</td>
                                                <td className="px-2 py-2 text-right text-gray-700 font-mono">{getCurrencySymbol()}{Number(component.cost || 0).toFixed(2)}</td>
                                            </tr>
                                        );
                                    })}
                                    {addedComponents.map((component, index) => {
                                        const componentKey = `added-${index}`;
                                        const isSelected = selectedComponents.includes(componentKey);
                                        return (
                                            <tr key={generateUniqueKey(component, index, 'added')} className={`hover:bg-gray-50 cursor-pointer ${isSelected ? "bg-blue-50" : ""}`} onClick={() => handleComponentSelection(componentKey)}>
                                                <td className="px-2 py-2"><input type="checkbox" checked={isSelected} onChange={() => handleComponentSelection(componentKey)} className="w-3.5 h-3.5 text-blue-600 rounded" onClick={(e) => e.stopPropagation()} /></td>
                                                <td className="px-2 py-2 font-medium text-gray-900">{component.name}</td>
                                                <td className="px-2 py-2 text-right text-gray-700 font-mono">{getCurrencySymbol()}{Number(component.cost || 0).toFixed(2)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-4">
                            <span className="text-sm text-gray-500">Selected: {selectedComponents.length}</span>
                            <div className="flex gap-2">
                                <BtnOutline onClick={() => setShowAddGroupPopup(false)}>Cancel</BtnOutline>
                                <BtnPrimary onClick={handleCreateGroup} disabled={!groupName.trim() || selectedComponents.length === 0}>
                                    <IconCheck className="w-4 h-4" /> Create Group
                                </BtnPrimary>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Sidebar + Main Layout ─── */}
            <Navigation currentUser={currentUser} activeTab={activeTab} onTabChange={onTabChange} onLogout={handleLogoutClick} onShowUserManagement={onShowUserManagement} sidebarOpen={sidebarOpen} onToggleSidebar={onToggleSidebar} />

            <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarOpen ? "lg:ml-64" : "lg:ml-0"}`}>
                <Header currentUser={currentUser} onTestAPI={() => { }} onToggleSidebar={onToggleSidebar} />

                <main className="flex-1 p-4 md:p-6 overflow-auto">
                    <div className="max-w-7xl mx-auto w-full">

                        {/* ─── Page Header: Title + Subtitle only ─── */}
                        <div className="mb-6">
                            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Components Management</h1>
                            {(loadingComponents || loadingAddedComponents) && (
                                <div className="mt-2">
                                    <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs font-medium border border-amber-200 inline-flex items-center gap-1">
                                        <IconSpinner className="w-3 h-3" /> Loading…
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* ─── Tabs ─── */}
                        {(apiComponents.length > 0 || apiGroupReports.length > 0) && (
                            <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
                                <button onClick={() => { setShowGlobalReport(true); setShowGroupReport(false); }} className={`px-4 py-2.5 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${showGlobalReport ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
                                    All Components
                                </button>
                                <button onClick={() => { setShowGroupReport(true); setShowGlobalReport(false); }} className={`px-4 py-2.5 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${showGroupReport ? "border-purple-600 text-purple-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
                                    Component Groups
                                </button>
                            </div>
                        )}

                        {/* ======================================================================
                            GLOBAL REPORT SECTION
                            ====================================================================== */}
                        {showGlobalReport && (
                            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                                {/* ─── Card Header: Title + Unified Toolbar ─── */}
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-4 md:p-6 border-b border-gray-100">
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-900">Components Overview</h2>
                                        <p className="text-gray-500 text-sm mt-0.5">{filteredUniqueComponents.length} of {apiComponents.length + addedComponents.length} components shown</p>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <BtnOutline onClick={() => setShowExcelImportPopup(true)}>
                                            <IconUpload className="w-4 h-4" /> <span className="hidden sm:inline">Import (Excel)</span>
                                        </BtnOutline>
                                        <BtnPrimary onClick={() => setShowAddComponentModal(true)}>
                                            <IconPlus className="w-4 h-4" /> <span className="hidden sm:inline">Add Component</span>
                                        </BtnPrimary>
                                        <BtnSecondary onClick={handleAddGroupClick}>
                                            <IconGroup className="w-4 h-4" /> <span className="hidden sm:inline">Create Group</span>
                                        </BtnSecondary>
                                        <BtnOutline onClick={downloadGlobalReportCSV}>
                                            <IconDownload className="w-4 h-4" /> <span className="hidden sm:inline">Export Report (CSV)</span>
                                        </BtnOutline>
                                        {selectedForBulkDelete.length > 0 && (
                                            <BtnDanger onClick={handleBulkDelete}>
                                                <IconTrash className="w-4 h-4" /> Delete Selected ({selectedForBulkDelete.length})
                                            </BtnDanger>
                                        )}
                                    </div>
                                </div>

                                {/* ─── Controls Row ─── */}
                                <div className="p-4 md:px-6 bg-gray-50/50 border-b border-gray-100">
                                    <div className="flex flex-col md:flex-row md:items-center gap-3">
                                        {/* Search */}
                                        <div className="relative flex-1 max-w-sm">
                                            <IconSearch className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                            <input type="text" placeholder="Search components…" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white" />
                                        </div>
                                        {/* Source Filter */}
                                        <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[130px]">
                                            <option value="all">All Sources</option>
                                            <option value="api">API</option>
                                            <option value="added">Added</option>
                                            <option value="excel">Excel</option>
                                        </select>
                                        {/* Sort */}
                                        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[140px]">
                                            <option value="oldest">Sort: Oldest First</option>
                                            <option value="newest">Sort: Newest First</option>
                                            <option value="name">Sort: Name</option>
                                            <option value="cost">Sort: Cost</option>
                                            <option value="availability">Sort: Availability</option>
                                            <option value="gravity">Sort: Sp. Gravity</option>
                                        </select>
                                        {/* Total shown */}
                                        <div className="text-sm text-gray-500 whitespace-nowrap ml-auto">
                                            Total shown: <span className="font-semibold text-gray-700">{filteredUniqueComponents.length}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* ─── Table Content ─── */}
                                <div className="p-4 md:p-6">
                                    {loadingComponents || loadingAddedComponents ? (
                                        <div className="text-center py-12">
                                            <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                                <IconSpinner className="w-7 h-7 text-blue-600" />
                                            </div>
                                            <h3 className="text-base font-semibold text-gray-900 mb-1">Loading Components</h3>
                                            <p className="text-gray-500 text-sm">
                                                {loadingComponents ? 'API Components' : ''}{loadingComponents && loadingAddedComponents ? ' and ' : ''}{loadingAddedComponents ? 'Added Components' : ''}
                                            </p>
                                        </div>
                                    ) : filteredUniqueComponents.length === 0 ? (
                                        /* Empty State with CTAs */
                                        <div className="text-center py-12">
                                            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                                <IconDocument className="w-7 h-7 text-gray-400" />
                                            </div>
                                            <h3 className="text-base font-semibold text-gray-900 mb-1">No components found</h3>
                                            <p className="text-gray-500 text-sm mb-6">{searchTerm || sourceFilter !== "all" ? "Try adjusting your search or filters" : "Get started by adding your first component"}</p>
                                            <div className="flex items-center justify-center gap-3">
                                                <BtnPrimary onClick={() => setShowAddComponentModal(true)}><IconPlus className="w-4 h-4" /> Add Component</BtnPrimary>
                                                <BtnOutline onClick={() => setShowExcelImportPopup(true)}><IconUpload className="w-4 h-4" /> Import (Excel)</BtnOutline>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            {/* Selection Controls */}
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-3">
                                                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                                                        <input type="checkbox" checked={selectedForBulkDelete.length === filteredUniqueComponents.length && filteredUniqueComponents.length > 0} onChange={handleSelectAllForBulkDelete} className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500" />
                                                        Select all on page
                                                    </label>
                                                    {selectedForBulkDelete.length > 0 && (
                                                        <button onClick={() => setSelectedForBulkDelete([])} className="text-xs text-blue-600 hover:text-blue-800 font-medium">Clear selection</button>
                                                    )}
                                                </div>
                                                {selectedForBulkDelete.length > 0 && (
                                                    <span className="text-sm font-medium text-blue-700 bg-blue-50 px-2.5 py-1 rounded-full">Selected: {selectedForBulkDelete.length}</span>
                                                )}
                                            </div>

                                            {/* Data Table */}
                                            <div className="overflow-x-auto rounded-lg border border-gray-200">
                                                <table className="min-w-full text-sm">
                                                    <thead>
                                                        <tr className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                                                            <th className="w-10 px-3 py-3"><input type="checkbox" checked={selectedForBulkDelete.length === filteredUniqueComponents.length && filteredUniqueComponents.length > 0} onChange={handleSelectAllForBulkDelete} className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500" /></th>
                                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Component Name</th>
                                                            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Cost ({getCurrencySymbol()})</th>
                                                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Sp. Gravity</th>
                                                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Properties</th>
                                                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Source</th>
                                                            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100">
                                                        {currentGlobalItems.map((component, index) => {
                                                            const isAddedComponent = component.source === "manual";
                                                            const isApiComponent = component.source === "api";
                                                            const isExcelImported = component.source === "excel-imported";
                                                            const componentId = component._id || component.id;
                                                            const isSelectedForBulkDelete = selectedForBulkDelete.includes(componentId);
                                                            const propCount = (component.selectedProperties || []).length + (component.selectedGroups || []).length;
                                                            return (
                                                                <tr key={componentId || generateUniqueKey(component, index, 'global')} className={`transition-colors ${isSelectedForBulkDelete ? 'bg-blue-50/60' : index % 2 === 1 ? 'bg-gray-50/30' : ''} hover:bg-blue-50/40`}>
                                                                    <td className="px-3 py-3"><input type="checkbox" checked={isSelectedForBulkDelete} onChange={() => handleBulkDeleteSelection(componentId)} className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500" /></td>
                                                                    <td className="px-4 py-3">
                                                                        <div className="flex items-center gap-2.5">
                                                                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center flex-shrink-0 border border-blue-200/50">
                                                                                <span className="text-xs font-bold text-blue-600">{component.name?.charAt(0)?.toUpperCase() || '?'}</span>
                                                                            </div>
                                                                            <div className="min-w-0">
                                                                                <div className="font-medium text-gray-900 text-sm truncate max-w-[200px]" title={component.name}>{component.name}</div>
                                                                                {component.costUnit && <div className="text-xs text-gray-400">{component.costUnit}</div>}
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-4 py-3 text-right"><span className="font-mono font-medium text-gray-900">{getCurrencySymbol(component.costUnit)}{Number(component.cost || 0).toFixed(2)}</span></td>
                                                                    <td className="px-4 py-3 text-center">
                                                                        {component.gravity ? <span className="inline-flex px-2 py-0.5 rounded-md bg-amber-50 text-amber-800 text-xs font-mono font-medium border border-amber-200/50">{Number(component.gravity).toFixed(4)}</span> : <span className="text-gray-300 text-xs">—</span>}
                                                                    </td>
                                                                    <td className="px-4 py-3 text-center hidden md:table-cell">
                                                                        {propCount > 0 ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-medium"><IconClipboard className="w-3 h-3" />{propCount}</span> : <span className="text-gray-300 text-xs">—</span>}
                                                                    </td>
                                                                    <td className="px-4 py-3 text-center hidden lg:table-cell">
                                                                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${isApiComponent ? 'bg-green-50 text-green-700 border border-green-200/50' : isExcelImported ? 'bg-orange-50 text-orange-700 border border-orange-200/50' : 'bg-purple-50 text-purple-700 border border-purple-200/50'}`}>
                                                                            {isApiComponent ? 'API' : isExcelImported ? 'Excel' : 'Manual'}
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-4 py-3">
                                                                        <div className="flex items-center gap-1.5 justify-end">
                                                                            <button onClick={() => handleEditComponent(component, isAddedComponent)} className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-white border border-gray-200 text-gray-600 rounded-lg text-xs font-medium hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-all"><IconEdit className="w-3 h-3" />Edit</button>
                                                                            <button onClick={() => handleDeleteComponent(component, isAddedComponent)} className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-white border border-gray-200 text-gray-600 rounded-lg text-xs font-medium hover:bg-red-50 hover:border-red-300 hover:text-red-700 transition-all"><IconTrash className="w-3 h-3" />Delete</button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>

                                            {/* Pagination */}
                                            {totalPages > 1 && (
                                                <div className="mt-5 flex flex-col sm:flex-row items-center justify-between gap-3">
                                                    <div className="text-sm text-gray-500">Showing {startIndex + 1}–{Math.min(endIndex, filteredUniqueComponents.length)} of {filteredUniqueComponents.length}</div>
                                                    <div className="flex items-center gap-1">
                                                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${currentPage === 1 ? "bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}>Previous</button>
                                                        {getPageNumbers().map((page) => (
                                                            <button key={`page-${page}`} onClick={() => handlePageChange(page)} className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${currentPage === page ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}>{page}</button>
                                                        ))}
                                                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${currentPage === totalPages ? "bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}>Next</button>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ======================================================================
                            GROUP REPORT SECTION
                            ====================================================================== */}
                        {showGroupReport && (
                            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-4 md:p-6 border-b border-gray-100">
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-900">Component Groups</h2>
                                        <p className="text-gray-500 text-sm mt-0.5">{apiGroupReports.length} groups created</p>
                                    </div>
                                    <BtnSecondary onClick={handleAddGroupClick}><IconGroup className="w-4 h-4" /> Create Group</BtnSecondary>
                                </div>

                                <div className="p-4 md:p-6">
                                    {loadingGroupReports ? (
                                        <div className="text-center py-12">
                                            <div className="w-14 h-14 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                                <IconSpinner className="w-7 h-7 text-purple-600" />
                                            </div>
                                            <h3 className="text-base font-semibold text-gray-900 mb-1">Loading Groups</h3>
                                            <p className="text-gray-500 text-sm">Fetching your group reports…</p>
                                        </div>
                                    ) : apiGroupReports.length === 0 ? (
                                        <div className="text-center py-12">
                                            <div className="w-14 h-14 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                                <IconGroup className="w-7 h-7 text-purple-400" />
                                            </div>
                                            <h3 className="text-base font-semibold text-gray-900 mb-1">No groups created yet</h3>
                                            <p className="text-gray-500 text-sm mb-6">Create your first group to organize component sets</p>
                                            <BtnPrimary onClick={handleAddGroupClick} className="mx-auto"><IconPlus className="w-4 h-4" /> Create Your First Group</BtnPrimary>
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            {apiGroupReports.map((report) => (
                                                <div key={report._id} className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                                                    <div className="p-4 cursor-pointer transition-colors hover:bg-gray-100" onClick={() => toggleGroupReportExpansion(report._id)}>
                                                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm flex-shrink-0 border border-gray-200">
                                                                    <IconCheck className="w-5 h-5 text-green-600" />
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <h3 className="text-base font-semibold text-gray-900 truncate">{report.name}</h3>
                                                                    <div className="flex flex-wrap items-center gap-2 mt-1">
                                                                        <span className="bg-white text-gray-600 px-2 py-0.5 rounded text-xs font-medium border border-gray-200">{report.components.length} components</span>
                                                                        <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded text-xs font-medium border border-green-200/50">Total: {report.currency}{report.totalCost.toFixed(2)}</span>
                                                                        <span className="text-gray-400 text-xs">{new Date(report.createdAt).toLocaleDateString()}</span>
                                                                    </div>
                                                                    {report.description && <p className="text-gray-500 text-xs mt-1 truncate">{report.description}</p>}
                                                                </div>
                                                            </div>
                                                            <div className="flex gap-2 flex-shrink-0">
                                                                <button onClick={(e) => { e.stopPropagation(); handleEditGroup(report); }} className="inline-flex items-center gap-1 px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-xs font-medium hover:bg-blue-50 hover:border-blue-300 transition-all"><IconEdit className="w-3.5 h-3.5" /> Edit</button>
                                                                <button onClick={(e) => { e.stopPropagation(); handleDeleteGroupReport(report._id); }} className="inline-flex items-center gap-1 px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-xs font-medium hover:bg-red-50 hover:border-red-300 hover:text-red-700 transition-all"><IconTrash className="w-3.5 h-3.5" /> Delete</button>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {expandedGroupReport === report._id && (
                                                        <div className="border-t border-gray-200 bg-white">
                                                            <div className="p-4 md:p-6">
                                                                <h4 className="text-sm font-semibold text-gray-900 mb-3">Group Components</h4>
                                                                <div className="overflow-x-auto rounded-lg border border-gray-200">
                                                                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                                                                        <thead className="bg-gray-50">
                                                                            <tr>
                                                                                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Component Name</th>
                                                                                <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">Cost ({report.currency})</th>
                                                                                <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">Volume</th>
                                                                                <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">Percentage</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody className="divide-y divide-gray-100">
                                                                            {report.components.map((component, index) => (
                                                                                <tr key={`group-component-${report._id}-${index}`} className={`${index % 2 === 1 ? 'bg-gray-50/30' : ''} hover:bg-blue-50/30`}>
                                                                                    <td className="px-4 py-2.5 font-medium text-gray-900 truncate max-w-[200px]">{component.name}</td>
                                                                                    <td className="px-4 py-2.5 text-right text-gray-600 font-mono">{report.currency}{component.cost.toFixed(2)}</td>
                                                                                    <td className="px-4 py-2.5 text-right text-gray-600">{component.volume} {report.volumeUnit}</td>
                                                                                    <td className="px-4 py-2.5 text-right text-gray-600 font-mono">{component.percentage?.toFixed(1)}%</td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </main>
            </div>
        </div>
    )
}

export default ComponentsPage;
